import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CartContext } from '../CartContext';
import { LoadingContext } from '../LoadingContext';
import './ProductDetail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faEye, faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import {
  faFacebookF,
  faFacebookMessenger,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons';
// import {
//   FacebookIcon,
//   FacebookMessengerIcon,
//   FacebookMessengerShareButton,
//   FacebookShareButton,
//   WhatsappIcon,
//   WhatsappShareButton
// } from "react-share";
import { AuthContext } from "../AuthContext";
import { trackEvent } from '../Analytics';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FACEBOOK_APP_ID = '100049133691033';

const ProductPriceDisplay = ({ product, selectedVariant, promoPrice }) => {
  const formatCurrency = (price) => {
    return price ? parseFloat(price).toFixed(2) : '0.00';
  };

  const isPriceValid = (price) => price !== null && price !== undefined && price > 0;

  let hasVariantWithInvalidPrice = false;
  let validVariantPrices = [];

  if (product.variants && product.variants.length > 0) {
    validVariantPrices = product.variants
      .map(variant => variant.price)
      .filter(price => isPriceValid(price));

    hasVariantWithInvalidPrice = product.variants.some(variant => !isPriceValid(variant.price));
  }

  //const hasValidBatchStock = (product.batchStockQuantity !== null && product.batchStockQuantity > 0);
  const hasValidStock = (product.stockQuantity !== null && product.stockQuantity > 0);
  const isPreOrder = !hasValidStock ||
    hasVariantWithInvalidPrice ||
    ((!product.variants || product.variants.length === 0) && !isPriceValid(product.price));

  // Promotion for the current selection (variant-specific, or product-level)
  const effectivePromo = promoPrice || null;
  // The price the promo is discounting from
  const promoBasePrice = selectedVariant ? selectedVariant.price : product.price;

  let priceElement = null;

  if (effectivePromo && isPriceValid(promoBasePrice)) {
    const discount = Math.min(99, Math.floor((1 - effectivePromo / promoBasePrice) * 100));
    priceElement = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em' }}>
          RM {formatCurrency(promoBasePrice)}
        </span>
        <span style={{ color: '#e53935' }}>RM {formatCurrency(effectivePromo)}</span>
        {discount > 0 && (
          <span style={{
            background: '#e53935', color: '#fff',
            fontSize: '0.7em', fontWeight: '700',
            padding: '2px 8px', borderRadius: '12px'
          }}>
            -{discount}% OFF
          </span>
        )}
      </span>
    );
  } else if (selectedVariant && isPriceValid(selectedVariant.price)) {
    priceElement = <span>RM {formatCurrency(selectedVariant.price)}</span>;
  } else if (product.variants && product.variants.length > 0 && validVariantPrices.length > 0 && !selectedVariant) {
    const minPrice = Math.min(...validVariantPrices);
    const maxPrice = Math.max(...validVariantPrices);
    if (minPrice === maxPrice) {
      priceElement = <span>RM {formatCurrency(minPrice)}</span>;
    } else {
      priceElement = <span>RM {formatCurrency(minPrice)} - RM {formatCurrency(maxPrice)}</span>;
    }
  } else if (isPriceValid(product.price)) {
    priceElement = <span>RM {formatCurrency(product.price)}</span>;
  }

  return (
    <h3>
      {priceElement}
      {isPreOrder && (
        <span className="pre-order-tag">Pre-order</span>
      )}
    </h3>
  );
};

function ProductDetail() {
  const { productCode } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useContext(CartContext);
  const { setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { userInfo, authFetch } = useContext(AuthContext);
  const [selectedSpecValues, setSelectedSpecValues] = useState({});
  const [availableSpecValues, setAvailableSpecValues] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Close the enlarged-image lightbox with Escape, and lock page scroll while open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isLightboxOpen]);
  const [isPreOrder, setIsPreOrder] = useState(false);
  const [promoMap, setPromoMap] = useState({});

  useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        xfbml: true,
        version: 'v18.0'
      });
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      document.head.appendChild(script);
    }

  }, []);

  const formatDescription = (description) => {
    if (!description) return 'N/A';

    return description.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < description.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        // Send the token when one exists so the API can exclude back-office
        // catalogue checks from real customer view counts.
        const response = await authFetch(`${API_BASE_URL}/Products/code/${productCode}?count=true`);
        if (!response.ok) {
          throw new Error(`Failed to fetch product: ${response.statusText}`);
        }
        const data = await response.json();
        setProduct(data);

        const categoryResponse = await fetch(`${API_BASE_URL}/api/categories/${data.categoryID}`);
        if (!categoryResponse.ok) {
          throw new Error(`Failed to fetch category: ${categoryResponse.statusText}`);
        }
        const categoryData = await categoryResponse.json();
        setCategory(categoryData);

        document.title = `${data.name} - Ardency Construction and Trading Sdn. Bhd.`;

        //const hasValidBatchStock = (data.batchStockQuantity !== null && data.batchStockQuantity > 0);
        const hasValidStock = (data.stockQuantity !== null && data.stockQuantity > 0);

        let hasVariantWithInvalidPrice = false;
        if (data.variants && data.variants.length > 0) {
          hasVariantWithInvalidPrice = data.variants.some(
            variant => variant.price === null || variant.price === undefined || variant.price <= 0
          );
        }

        const shouldBePreOrder = !hasValidStock ||
          hasVariantWithInvalidPrice ||
          ((!data.variants || data.variants.length === 0) &&
            (data.price === null || data.price === undefined || data.price <= 0));

        setIsPreOrder(shouldBePreOrder);

        if (data.moq && data.moq > 1) {
          setQuantity(data.moq);
        }

        if (data.variants && data.variants.length > 0) {
          initializeSpecifications(data);
        }

        if (userInfo) {
          const userId = userInfo.userId;
          const wishlistResponse = await fetch(
            `${API_BASE_URL}/api/Wishlists/?productId=${data.productID}&userId=${userId}`
          );
          if (wishlistResponse.ok) {
            const isWishlistedData = await wishlistResponse.json();
            setIsWishlisted(!!isWishlistedData);
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    // Promotions may target the product's base price or a specific variant, so we
    // keep a map: p<productId> -> price, v<variantId> -> price
    const fetchPromo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/Promotions`);
        if (res.ok) {
          const promos = await res.json();
          const map = {};
          promos
            .filter(p => p.productCode === productCode)
            .forEach(p => {
              if (p.productId || p.variantId) {
                map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
              }
            });
          setPromoMap(map);
        }
      } catch (e) { }
    };

    if (productCode) {
      fetchProduct();
      fetchPromo();
    }

    return () => {
      document.title = 'Ardency Construction & Trading Sdn. Bhd.';
    };

  }, [navigate, productCode]);

  const initializeSpecifications = (product) => {
    if (!product.specifications || !product.variants) return;

    const specValuesBySpec = {};

    product.specifications.forEach(spec => {
      if (spec.specificationValues && spec.specificationValues.length > 0) {
        specValuesBySpec[spec.specificationId] = spec.specificationValues.map(value => ({
          specValueId: value.specificationValueId,
          value: value.value,
          available: true
        }));
      }
    });

    setAvailableSpecValues(specValuesBySpec);
  };


  const isValueAvailable = (specId, specValueId) => {
    if (!product || !product.variants || product.variants.length === 0) return true;

    if (Object.keys(selectedSpecValues).length === 0) return true;

    const testSelection = { ...selectedSpecValues, [specId]: specValueId };

    return product.variants.some(variant =>
      Object.entries(testSelection).every(([testSpecId, testSpecValueId]) =>
        variant.variantValues.some(variantValue =>
          variantValue.specValueId === testSpecValueId
        )
      )
    );
  };

  useEffect(() => {
    if (!product || !product.variants || product.variants.length === 0) return;

    const newAvailableValues = {};

    product.specifications.forEach(spec => {
      const specId = spec.specificationId;
      newAvailableValues[specId] = spec.specificationValues.map(specValue => ({
        specValueId: specValue.specificationValueId,
        value: specValue.value,
        available: isValueAvailable(specId, specValue.specificationValueId)
      }));
    });

    setAvailableSpecValues(newAvailableValues);

    if (Object.keys(selectedSpecValues).length === product.specifications.length) {
      const matchingVariant = product.variants.find(variant =>
        Object.entries(selectedSpecValues).every(([specId, specValueId]) =>
          variant.variantValues.some(variantValue =>
            variantValue.specValueId === specValueId
          )
        )
      );

      // Detect an actual change of variant so we don't clobber a quantity the
      // customer typed while staying on the same variant.
      const newVariantId = matchingVariant?.variantId ?? null;
      const variantChanged = (selectedVariant?.variantId ?? null) !== newVariantId;

      setSelectedVariant(matchingVariant || null);

      if (variantChanged) {
        // Start at the newly selected variant's minimum. A variant MOQ takes
        // precedence over the product MOQ; with neither, the minimum is 1.
        const newMoq = matchingVariant?.moq || product?.moq || 1;
        setQuantity(newMoq);
      }
    } else {
      setSelectedVariant(null);
    }
  }, [selectedSpecValues, product]);

  const handleSpecValueSelect = (specId, specValueId) => {
    if (!isValueAvailable(specId, specValueId) && Object.keys(selectedSpecValues).length > 0) {
      return;
    }

    const newSelectedValues = { ...selectedSpecValues };

    if (newSelectedValues[specId] === specValueId) {
      delete newSelectedValues[specId];
    } else {
      newSelectedValues[specId] = specValueId;
    }

    setSelectedSpecValues(newSelectedValues);
  };

  if (!product) {
    return <div className="product-detail-loading">Loading...</div>;
  }

  const handleAddToCart = () => {
    trackEvent('add_to_cart', {
      product_id: product.productID,
      product_code: product.productCode,
      product_name: product.name,
      quantity: quantity,
      category: category ? category.name : 'Unknown',
      has_variant: !!(selectedVariant),
      variant_details: selectedVariant ? Object.values(selectedSpecValues).join(',') : null
    });

    if (!userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login to add to cart.',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // Only enforce a minimum when there actually is one (MOQ is optional).
    // A selected variant's MOQ takes precedence over the product's.
    const minOrderQty = selectedVariant?.moq || product.moq;
    if (minOrderQty && minOrderQty > 1 && quantity < minOrderQty) {
      Swal.fire({
        title: 'Minimum Order Quantity',
        text: `The minimum order quantity for this option is ${minOrderQty}.`,
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    if (!selectedVariant && product.variants && product.variants.length > 0) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select all required specifications',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // A promotion (variant-specific or product-level) always wins; otherwise use the
    // selected variant's price, falling back to the product's base price.
    const promo = getEffectivePromo();
    const basePrice = selectedVariant && selectedVariant.price !== null
      ? selectedVariant.price
      : product.price;
    const price = promo ?? basePrice;

    addToCart({
      ...product,
      selectedVariant: selectedVariant,
      price: price,
      // Variant MOQ/UOM (both optional) take precedence over the product's
      moq: selectedVariant?.moq || product.moq || 1,
      uom: selectedVariant?.uom || product.uom || ""
    }, quantity);

    Swal.fire({
      title: 'Added to Cart!',
      text: `${quantity} items added successfully`,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false
    });

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 3000);
  };

  // A selected variant's MOQ/UOM (both optional) take precedence over the product's.
  const getEffectiveMoq = () => selectedVariant?.moq || product?.moq || 1;
  const getEffectiveUom = () => selectedVariant?.uom || product?.uom || "";

  // Promotion for the current selection: a variant-specific promo when a variant is
  // chosen, otherwise the product-level promo.
  const getEffectivePromo = () => {
    const key = selectedVariant?.variantId
      ? `v${selectedVariant.variantId}`
      : `p${product?.productID}`;
    return promoMap[key] ?? null;
  };

  const handleIncrement = () => {
    // MOQ is optional - fall back to 1 so a blank MOQ can never produce NaN
    const step = product.isIncreaseByMOQ ? getEffectiveMoq() : 1;
    setQuantity(quantity + step);
  };

  const handleDecrement = () => {
    const minQty = getEffectiveMoq();
    if (quantity > minQty) {
      const step = product.isIncreaseByMOQ ? getEffectiveMoq() : 1;
      setQuantity(Math.max(minQty, quantity - step));
    }
  };

  const toggleWishlist = async () => {
    if (!userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login to add to wishlist.',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    try {
      const userId = userInfo.userId;
      const response = await authFetch(`${API_BASE_URL}/api/Wishlists?productId=${product.productID}&userId=${userId}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Failed to update wishlist: ${response.statusText}`);
      }

      setIsWishlisted(!isWishlisted);

      Swal.fire({
        title: isWishlisted ? 'Removed from Wishlist' : 'Added to Wishlist',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        title: 'Operation Failed',
        text: 'Failed to update wishlist.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  const isSpecValueSelected = (specId, specValueId) => {
    return selectedSpecValues[specId] === specValueId;
  };

  const isSpecValueAvailable = (specId, specValueId) => {
    const specValues = availableSpecValues[specId];
    if (!specValues) return false;

    const specValue = specValues.find(sv => sv.specValueId === specValueId);
    return specValue ? specValue.available : false;
  };

  const shareUrl = window.location.href;
  const shareTitle = encodeURIComponent(product.name);

  const shareFacebook = (e) => {
    e.preventDefault();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
  };

  const shareMessenger = (e) => {
    e.preventDefault();
    if (window.FB) {
      window.FB.ui({
        method: 'send',
        link: shareUrl,
      });
    } else {
      window.open(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=${FACEBOOK_APP_ID}`, '_blank', 'width=600,height=400');
    }
  };

  const shareWhatsApp = (e) => {
    e.preventDefault();
    window.open(`https://wa.me/?text=${shareTitle}%20${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleQuantityChange = (e) => {
    const minQty = getEffectiveMoq();
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && newQuantity >= 1) {
      setQuantity(newQuantity < minQty ? minQty : newQuantity);
    } else {
      setQuantity(minQty);
    }
  };


  return (
    <div className="product-detail-main-container">
      {/* Breadcrumb Section */}
      <div className="product-detail-breadcrumb">
        <Link to="/">Home</Link> &gt; {" "}
        <Link to="/category">Our Products</Link> &gt; {" "}
        <Link to={`/category/${product.categoryID}`}>{category ? category.name : 'Loading...'}</Link> &gt; {" "}
        <span>{product.name}</span>
      </div>

      {/* Product Main Section */}
      <div className="product-detail-container">
        <div className="product-detail-image">
          {/* {selectedVariant ? (
            <img
              src={`${API_BASE_URL}/products/image/${product.productID}`}
              alt={product.name}
              className="main-image"
            />
          ) : (
            <img
              src={currentImage || `${product.mainImageUrl}`}
              alt={product.name}
              className="main-image"
            />
          )} */}
          <img
            src={currentImage || `${product.mainImageUrl}`}
            alt={product.name}
            className="main-image"
            style={{ cursor: 'zoom-in' }}
            onClick={() => setIsLightboxOpen(true)}
            title="Click to enlarge"
          />

          {/* Lightbox / enlarged image overlay */}
          {isLightboxOpen && (
            <div
              onClick={() => setIsLightboxOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                cursor: 'zoom-out',
                padding: '20px'
              }}
            >
              <span
                onClick={() => setIsLightboxOpen(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '32px',
                  color: '#fff',
                  fontSize: '44px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontWeight: 300
                }}
                aria-label="Close"
              >
                &times;
              </span>

              <img
                src={currentImage || `${product.mainImageUrl}`}
                alt={product.name}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 0 40px rgba(0, 0, 0, 0.5)',
                  margin: 0
                }}
              />

              {/* Thumbnail strip inside the lightbox for switching images */}
              {product.additionalImages && product.additionalImages.length > 0 && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '10px',
                    background: 'rgba(0,0,0,0.4)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    maxWidth: '90vw',
                    overflowX: 'auto'
                  }}
                >
                  <img
                    src={`${product.mainImageUrl}`}
                    alt={`${product.name} - Main`}
                    onClick={() => setCurrentImage(null)}
                    style={{
                      width: '56px',
                      height: '56px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: currentImage === null ? '2px solid #fff' : '2px solid transparent',
                      opacity: currentImage === null ? 1 : 0.6
                    }}
                  />
                  {product.additionalImages.map((image, index) => (
                    <img
                      key={image.imageId}
                      src={`${image.imageUrl}`}
                      alt={`${product.name} - ${index + 1}`}
                      onClick={() => setCurrentImage(`${image.imageUrl}`)}
                      style={{
                        width: '56px',
                        height: '56px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        border: currentImage === `${image.imageUrl}` ? '2px solid #fff' : '2px solid transparent',
                        opacity: currentImage === `${image.imageUrl}` ? 1 : 0.6
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Thumbnails */}
          {product.additionalImages && product.additionalImages.length > 0 && (
            <div className="product-detail-thumbnails">
              <img
                src={`${product.mainImageUrl}`}
                alt={`${product.name} - Main`}
                className={currentImage === null ? "active" : ""}
                onClick={() => setCurrentImage(null)}
              />

              {product.additionalImages.map((image, index) => (
                <img
                  key={image.imageId}
                  src={`${image.imageUrl}`}
                  alt={`${product.name} - ${index + 1}`}
                  className={currentImage === `${image.imageUrl}` ? "active" : ""}
                  onClick={() => setCurrentImage(`${image.imageUrl}`)}
                />
              ))}
            </div>
          )}

          {/* Share and Wishlist Section */}
          <div className="product-detail-share-row">
            <div className="product-detail-share">

              <span>Share:</span>
              {/* <a>
                <FacebookShareButton url={shareUrl} >
                  <FacebookIcon size={32}/>
                </FacebookShareButton>
              </a>
              <a>
                <WhatsappShareButton url={shareUrl} >
                  <WhatsappIcon size={32}/>
                </WhatsappShareButton>
              </a>
              <a>
                <FacebookMessengerShareButton url={shareUrl} >
                  <FacebookMessengerIcon size={32}/>
                </FacebookMessengerShareButton>
              </a> */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                onClick={shareFacebook}
                className="share-button share-facebook"
                aria-label="Share on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
              <a
                href={`https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=${FACEBOOK_APP_ID}`}
                onClick={shareMessenger}
                className="share-button share-messenger"
                aria-label="Share on Messenger"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookMessenger} />
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                onClick={shareWhatsApp}
                className="share-button share-whatsapp"
                aria-label="Share on WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </a>
            </div>

            <div className="product-detail-wishlist">
              <a className="product-detail-button-wishlist" href="#" onClick={(e) => { e.preventDefault(); toggleWishlist(); }}>
                <FontAwesomeIcon icon={isWishlisted ? faHeartSolid : faHeartRegular} /> Add To Wish List
              </a>
            </div>
          </div>
        </div>

        {/* Product Details Section */}
        <div className="product-detail-details">
          <h2>{product.name}</h2>
          <ProductPriceDisplay product={product} selectedVariant={selectedVariant} promoPrice={getEffectivePromo()} />
          <div className="product-detail-description">
            <div className="product-detail-head">
              <p>Category:</p>
              <p>Description:</p>
              <p></p>
            </div>
            <div className="product-detail-tail">
              <p>{category ? category.name : 'Loading...'}</p>
              <p>{formatDescription(product.description)}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#4CAF50" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm.75 17.5v1.25h-1.5V18.5a4.502 4.502 0 01-3.75-4.5h1.5a3 3 0 003 3c1.25 0 2-.75 2-1.75s-.75-1.5-2.25-2c-2-.75-3.75-1.75-3.75-3.75 0-2 1.5-3.25 3.25-3.5V4.25h1.5V5.5c2 .25 3.5 1.75 3.5 3.75h-1.5a2 2 0 00-2-2c-1.25 0-2 .75-2 1.75s.75 1.5 2.25 2c2 .75 3.75 1.75 3.75 3.75s-1.5 3.25-3.5 3.5z"></path>
                  </svg>
                  Sold: {product.totalSales}
                </span>

                <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '20px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#E53935" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z"></path>
                  </svg>
                  Customer views: {product.totalView}
                </span>
              </div>
            </div>
          </div>

          {/* Product Specifications Section */}
          {product.variants && product.variants.length > 0 && product.specifications && product.specifications.length > 0 && (
            <div className="product-detail-specifications">
              {product.specifications.map(spec => (
                <div key={spec.specificationId} className="specification-section">
                  <h4>{spec.name}:</h4>
                  <div className="specification-values">
                    {spec.specificationValues?.map(specValue => {
                      const isSelected = isSpecValueSelected(spec.specificationId, specValue.specificationValueId);
                      const isAvailable = isSpecValueAvailable(spec.specificationId, specValue.specificationValueId);

                      return (
                        <div
                          key={specValue.specificationValueId}
                          className={`specification-box 
                            ${isSelected ? 'selected' : ''} 
                            ${!isAvailable && Object.keys(selectedSpecValues).length > 0 ? 'disabled' : ''}`
                          }
                          onClick={() =>
                            (isAvailable || Object.keys(selectedSpecValues).length === 0) &&
                            handleSpecValueSelect(spec.specificationId, specValue.specificationValueId)
                          }
                        >
                          {specValue.value}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {Object.keys(selectedSpecValues).length > 0 && (
                <button
                  className="reset-selections-button"
                  onClick={() => setSelectedSpecValues({})}
                >
                  Reset Selections
                </button>
              )}
            </div>
          )}

          {/* Selected Variant Section */}
          {Object.keys(selectedSpecValues).length > 0 && (
            <div className="product-detail-selected-variant">
              <span>Selected Variant: </span>
              {product.specifications
                .filter(spec => selectedSpecValues[spec.specificationId])
                .map((spec, index, array) => {
                  const selectedValue = spec.specificationValues.find(
                    val => val.specificationValueId === selectedSpecValues[spec.specificationId]
                  );
                  return (
                    <React.Fragment key={spec.specificationId}>
                      <span className="variant-value">{selectedValue ? selectedValue.value : ''}</span>
                      {index < array.length - 1 && <span style={{ fontWeight: 600 }}>, </span>}
                    </React.Fragment>
                  );
                })}
            </div>
          )}


          {/* Quantity and Add to Cart Section */}
          {!isPreOrder && (
            <div className="product-detail-quantity-selector">
              <label>Quantity:</label>
              <div className="quantity-value">
                <button onClick={handleDecrement}>-</button>
                <input type="number" value={quantity} min={getEffectiveMoq()} readOnly={product.isIncreaseByMOQ}
                  onBlur={handleQuantityChange}
                  onChange={(e) => setQuantity(e.target.value)} />
                <button onClick={handleIncrement}>+</button>
              </div>
              {getEffectiveUom() && (
                <div><b>{getEffectiveUom()}</b></div>
              )}
              <button
                className="product-detail-button-addcart"
                onClick={handleAddToCart}
                disabled={product.variants && product.variants.length > 0 && !selectedVariant}
              >
                Add to Cart
              </button>
            </div>
          )}

          {/* Pre-order products */}
          {isPreOrder && (
            <div className="product-detail-preorder-message">
              <p>This item is available for pre-order only. Please contact us for details and availability.</p>
            </div>
          )}

          <p>Any specific requirements, please feel free to contact us.</p>

          {/* WhatsApp and Inquire Section */}
          <div className="product-detail-other-buttons">
            <a
              href={`https://wa.me/customerphonenumber?text=Hello%2C%20I%20am%20interested%20in%20${product.name}.%0A%28https%3A%2F%2Fwww.testing.com%2Fproduct%2F${productCode}%29`}
              className="product-detail-button-whatapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Us
            </a>
            <Link to={`/product/${productCode}/inquiry`} className="product-detail-button-inquire">Inquire Now</Link>
          </div>
          <p><i>Disclaimer<br />
            All product images shown are for illustration purposes only. Actual products may vary in terms of brand, type, size, colour, version, and other relevant characteristics.</i></p>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
